import {Button, CircularProgress, Collapse, Grid, IconButton, makeStyles, TableCell, TableRow} from '@material-ui/core';
import {green} from '@material-ui/core/colors';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import moment from 'moment';
import {MouseEvent, useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import CRM from '../../APIs/CRM';
import constants from '../../constants';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../Core/mobx/RootStore';
import {QuotesHistory} from '../Quotes/QuotesHistory';
import RxDialog from '../Tools/RxDialog';
import {Contact} from '../types';

const {CUSTOMER_PORTAL_URL} = constants;

const useRowStyles = makeStyles({
    root: {
        '& > *': {
            borderBottom: 'unset',
        },
    },
    clickableRow: {
        cursor: 'pointer',
    },
});

const UwStatusToDisplay: {[key: string]: string} = {
    UNINITIALIZED: 'Uninitialized',
    CLOSED: 'Closed',
    OPEN_AP: 'Open (AP)',
};

export function ContactInfo(props: {record: any; contact: Contact}) {
    const {record, contact} = props;
    const [open, setOpen] = useState(false);
    const classes = useRowStyles();
    const history = useHistory();
    const parameterStore = useMemo(() => new ParameterStore(), []);
    const [loading, setLoading] = useState(false);
    const [showVerifyDialog, setShowVerifyDialog] = useState(false);
    const [verifyLink, setVerifyLink] = useState('');
    const [verifyEmail, setVerifyEmail] = useState('');
    const [verifyPhone, setVerifyPhone] = useState('');

    const getForm = async () => {
        setLoading(true);
        const list = await CRM.GetClientRecord(record.v.contact, record.v.formid);

        if (list) {
            await parameterStore.loadParamsFromSavedForm(list);
        }
        setLoading(false);
    };

    const onCloseVerifyDialog = async () => {
        setShowVerifyDialog(false);
    };

    const getVerifyLink = async () => {
        let respCode;
        const lookup = new Map<string, string>();
        const {approval} = record.v;
        approval.forEach((a: any) => {
            if (a != null) {
                if (!lookup.has(a.p)) {
                    lookup.set(a.p, a.v);
                }
            }
        });
        const email = lookup.get('emailAddress');
        const phNo = lookup.get('phoneNumber');
        email && setVerifyEmail(email);
        phNo && setVerifyPhone(phNo);
        if (record.v.formid && email) {
            respCode = await CRM.GetCustomerVerifyDetailCode(record.v.formid, email);
        }
        return respCode;
    };

    const sendEFTLink = async () => {
        const respCode = await getVerifyLink();
        if (respCode) {
            setShowVerifyDialog(true);
            const link = `${CUSTOMER_PORTAL_URL}/verify-bank/?c=${respCode.code}`;
            setVerifyLink(link);
        }
    };

    const onCreateFamilyPolicy = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        (async () => {
            window.open(`/agent/newform/2?familyFormId=${record.v.formid}&familyEmail=${record.v.contact}`, '_blank');
        })();
    };

    useEffect(() => {
        open && getForm();
    }, [open]);

    const name = record.v?.approval
        ?.filter((entry: {p: string; v: string}) => entry.p === 'firstName' || entry.p === 'lastName')
        ?.map((entry: {p: string; v: string}) => entry.v);

    const onOpenForm = () => {
        RootStore.agentPortalStore.reset();
        RootStore.assistStore.resetStore();
        history.push(`/agent/form/${record.v.contact}/${record.v.formid}`);
    };

    return (
        <>
            <TableRow className={[classes.root, classes.clickableRow].join(' ')} hover onClick={onOpenForm}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(!open);
                        }}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    {record.v.createTimestamp
                        ? moment(record.v.createTimestamp).format('MMM DD, YYYY hh:mm A')
                        : moment(record.v.created).format('MMM DD, YYYY')}
                </TableCell>
                <TableCell>{name && name[0] && name[1] ? `${name[0]} ${name[1]}` : record.v.name}</TableCell>
                <TableCell>
                    {record.v.formPreQual ? <CheckIcon style={{color: green[500]}} /> : <CloseIcon color="error" />}
                </TableCell>
                <TableCell>
                    {record.v.formQuote ? <CheckIcon style={{color: green[500]}} /> : <CloseIcon color="error" />}
                </TableCell>
                <TableCell>
                    {record.v.formPreApproval ? <CheckIcon style={{color: green[500]}} /> : <CloseIcon color="error" />}
                </TableCell>
                <TableCell>{UwStatusToDisplay[record.v.formProsperUWStatus || 'UNINITIALIZED']}</TableCell>
                <TableCell>
                    {!record.v.formProsperCaseStartDate
                        ? 'N/A'
                        : `${moment().diff(moment(record.v.formProsperCaseStartDate), 'days')} days`}
                </TableCell>

                <TableCell style={{display: 'flex', justifyContent: 'right', gap: 12}}>
                    <Button variant="outlined" color="primary" onClick={onCreateFamilyPolicy}>
                        + Family Policy
                    </Button>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {!loading ? (
                            <QuotesHistory hideQuoteData parameterStore={parameterStore} />
                        ) : (
                            <Grid alignItems={'center'} justify={'center'} style={{width: '100%'}}>
                                <CircularProgress />
                            </Grid>
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>
            <RxDialog
                open={showVerifyDialog}
                onClose={onCloseVerifyDialog}
                link={verifyLink}
                email={verifyEmail}
                setEmail={setVerifyEmail}
                phone={verifyPhone}
                setPhone={setVerifyPhone}
                type="verify"
            ></RxDialog>
        </>
    );
}
